import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MembershipPage() {
  const [tiers, setTiers] = useState([]);
  const [benefits, setBenefits] = useState([]);

  useEffect(() => {
    document.title = 'Club JCI Ltd - Membership';
    
    const fetchData = async () => {
      try {
        const [tiersRes, benefitsRes] = await Promise.all([
          axios.get(`${API}/membership-tiers`),
          axios.get(`${API}/member-benefits`)
        ]);
        setTiers(tiersRes.data);
        setBenefits(benefitsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-BD', {
      style: 'decimal',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div data-testid="membership-page" className="pt-24">
      {/* Hero Section */}
      <section className="section-padding marble-overlay" data-testid="membership-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-[#D4AF37] text-sm uppercase tracking-widest mb-4">Membership</p>
            <h1 className="text-4xl sm:text-5xl font-semibold text-[#F1F5F9] mb-6">
              Join the <span className="gold-text">Elite Circle</span>
            </h1>
            <p className="text-lg text-[#94A3B8] leading-relaxed">
              Choose the membership tier that aligns with your aspirations. 
              Each tier unlocks a world of exclusive privileges and opportunities.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Membership Tiers */}
      <section className="section-padding bg-[#0A192F]" data-testid="tiers-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {tiers.map((tier, index) => (
              <motion.div
                key={tier.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`tier-card glass-card rounded-sm overflow-hidden ${
                  tier.is_featured ? 'featured ring-1 ring-[#D4AF37]' : ''
                }`}
                data-testid={`tier-card-${tier.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="p-8">
                  <h3 className="text-xl font-semibold text-[#F1F5F9] mb-2">{tier.name}</h3>
                  
                  <div className="my-6">
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold gold-text">
                        {formatCurrency(tier.entry_fee)}
                      </span>
                      <span className="text-[#94A3B8] text-sm">BDT</span>
                    </div>
                    <p className="text-[#94A3B8] text-sm mt-1">Entry Fee</p>
                  </div>

                  <div className="mb-6 pb-6 border-b border-[rgba(212,175,55,0.1)]">
                    <p className="text-[#F1F5F9]">
                      {tier.monthly_fee === 0 ? (
                        <span className="text-[#D4AF37]">No Monthly Fee</span>
                      ) : (
                        <>
                          <span className="text-2xl font-semibold">{formatCurrency(tier.monthly_fee)}</span>
                          <span className="text-[#94A3B8] text-sm"> BDT/month</span>
                        </>
                      )}
                    </p>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {tier.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-[#94A3B8]">{benefit}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="/contact"
                    data-testid={`apply-${tier.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`w-full block text-center py-3 rounded-sm text-sm transition-all ${
                      tier.is_featured
                        ? 'btn-gold-filled'
                        : 'btn-gold'
                    }`}
                  >
                    Apply Now
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Overview */}
      <section className="section-padding" data-testid="benefits-overview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-[#F1F5F9] mb-4">
              Member <span className="gold-text">Benefits</span>
            </h2>
            <p className="text-[#94A3B8] max-w-2xl mx-auto">
              Every membership opens doors to exceptional experiences and opportunities.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="glass-card p-6 rounded-sm"
              >
                <h3 className="text-lg font-semibold text-[#D4AF37] mb-2">{benefit.title}</h3>
                <p className="text-sm text-[#94A3B8]">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-[#0A192F]" data-testid="membership-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-[#F1F5F9] mb-6">
              Ready to Begin Your Journey?
            </h2>
            <p className="text-[#94A3B8] mb-10 max-w-2xl mx-auto">
              Take the first step towards becoming part of Dhaka's most distinguished professional community.
            </p>
            <Link
              to="/contact"
              data-testid="membership-apply-cta"
              className="btn-gold-filled text-sm py-4 px-10 rounded-sm inline-flex items-center gap-2"
            >
              Inquire About Membership
              <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
